/**
 * Opens legal shop pages in a modal instead of navigating away.
 *
 * The links live inside richtext theme settings, so they are plain `<a href>` with no
 * hooks to hang behaviour on. The handle is read back out of the href and matched
 * against the dialogs rendered by `snippets/legal-page-dialog.liquid`. When no dialog
 * is present — page unpublished, snippet not rendered — the click is left alone and
 * the browser follows the link as usual.
 *
 * Interception is opt-in per region via `data-legal-dialog-scope`: the same pages are
 * also listed in the footer menu, where they should navigate normally.
 */
(() => {
  const DIALOG_SELECTOR = '[data-legal-dialog]';
  const SCOPE_SELECTOR = '[data-legal-dialog-scope]';

  /**
   * Prefers the hoisted copy under <body>, since a dialog still sitting in the section
   * markup would open off-centre.
   *
   * @param {string} handle
   * @returns {HTMLDialogElement | null}
   */
  const dialogFor = (handle) => {
    const selector = `${DIALOG_SELECTOR}[data-legal-dialog-handle="${CSS.escape(handle)}"]`;
    return document.body.querySelector(`:scope > ${selector}`) ?? document.querySelector(selector);
  };

  /**
   * Reparents the dialogs to <body> so they centre on the viewport.
   *
   * The newsletter card that holds the KVKK link carries a `backdrop-filter`, which
   * makes it a containing block for fixed-position descendants and knocks the modal
   * off centre. Hoisting also lifts the dialog out of the newsletter <form>, so its
   * buttons can never take part in that form's submission.
   */
  const hoistDialogs = () => {
    for (const dialog of document.querySelectorAll(DIALOG_SELECTOR)) {
      if (dialog.parentElement === document.body) continue;

      // A section re-render in the theme editor reinstates a fresh copy alongside the
      // one already hoisted, so the previous one is dropped rather than accumulated.
      const handle = CSS.escape(dialog.dataset.legalDialogHandle ?? '');
      const previous = document.body.querySelector(
        `:scope > ${DIALOG_SELECTOR}[data-legal-dialog-handle="${handle}"]`
      );
      previous?.remove();

      document.body.append(dialog);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hoistDialogs);
  } else {
    hoistDialogs();
  }

  // Theme editor section re-renders drop a new copy back into the section markup.
  document.addEventListener('shopify:section:load', hoistDialogs);

  /**
   * @param {HTMLAnchorElement} link
   * @returns {string | null} The page handle the link points at, if it is a page link.
   */
  const handleFromLink = (link) => {
    const href = link.getAttribute('href');
    if (!href) return null;

    // Same-origin only: `new URL` needs a base for the root-relative hrefs used here.
    let path;
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      path = url.pathname;
    } catch (error) {
      return null;
    }

    // Matches /pages/handle as well as locale-prefixed /tr/pages/handle.
    const match = path.match(/\/pages\/([^/]+)\/?$/);
    return match ? match[1] : null;
  };

  /**
   * The checkbox belonging to the consent label the dialog was opened from. Several
   * forms can share one hoisted dialog per handle (contact form + footer newsletter),
   * so the id baked into the accept button only fits whichever copy survived hoisting;
   * the opener's own checkbox has to win over it.
   *
   * @type {HTMLInputElement | null}
   */
  let openerCheckbox = null;

  document.addEventListener('click', (event) => {
    const accept = event.target.closest?.('[data-legal-dialog-accept]');
    if (accept) {
      const checkbox = openerCheckbox ?? document.getElementById(accept.dataset.legalDialogAccept);
      openerCheckbox = null;

      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        // The checkbox carries `required`, so let the form clear its invalid state.
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }

      accept.closest('dialog')?.close();
      return;
    }

    const close = event.target.closest?.('[data-legal-dialog-close]');
    if (close) {
      close.closest('dialog')?.close();
      return;
    }

    const link = event.target.closest?.(`${SCOPE_SELECTOR} a[href]`);
    if (!link) return;

    // Leave modified clicks alone so opening in a new tab still works.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    const handle = handleFromLink(link);
    if (!handle) return;

    const dialog = dialogFor(handle);
    if (!dialog) return;

    openerCheckbox = link.closest('label')?.querySelector('input[type="checkbox"]') ?? null;

    // preventDefault keeps the browser from navigating; stopPropagation keeps the
    // click from reaching the newsletter consent <label> these links sit inside.
    event.preventDefault();
    event.stopPropagation();
    dialog.showModal();
  });

  // Clicking the backdrop lands on the dialog element itself, never on its children.
  document.addEventListener('click', (event) => {
    const dialog = event.target.closest?.(DIALOG_SELECTOR);
    if (dialog && event.target === dialog) dialog.close();
  });
})();
